import { BaseApiService } from '@/shared/api/services/base.service';
import type {
  CatalogBrandsResponse,
  CatalogCategoriesResponse,
  CatalogLinesResponse,
  CatalogMarket,
  CatalogNotices,
  CatalogProductsResponse,
  Manufacturer,
  Product,
  ProductCategory,
  ProductLine,
} from '@/types/catalog';

export interface CatalogProductsPage {
  products: Product[];
  /** Cursor for the next page, or null at the end. */
  nextCursor: string | null;
  notices: CatalogNotices | null;
}

/**
 * The one catalogue contract, for every library.
 *
 * Replaces the per-library services (paint-colors, siding): the endpoints are
 * the same four whatever the category, so a service per library was the same
 * code with the paths changed.
 */
class CatalogService extends BaseApiService {
  /**
   * Which libraries exist, how deep each goes and what it is faceted by.
   *
   * This is what a picker reads instead of carrying a hardcoded category list
   * that has to ship to change.
   */
  async getCategories(): Promise<{
    categories: ProductCategory[];
    notices: CatalogNotices | null;
  }> {
    const response = await this.httpGet<CatalogCategoriesResponse>(
      '/v1/catalog/categories'
    );

    return {
      categories: response.data,
      notices: response.meta?.notices ?? null,
    };
  }

  /**
   * The shelf, arranged for a country.
   *
   * With no `country` the API sorts the makers known to sell where the user's
   * house is to the top and says which country that was; with one, it returns
   * only the makers known to sell there.
   */
  async getBrands(
    category: string,
    country?: string | null
  ): Promise<{
    brands: Manufacturer[];
    notices: CatalogNotices | null;
    countryCode: string | null;
    markets: CatalogMarket[];
  }> {
    const query = new URLSearchParams({ category });
    if (country) query.set('country', country);

    const response = await this.httpGet<CatalogBrandsResponse>(
      `/v1/catalog/brands?${query.toString()}`
    );

    return {
      brands: response.data,
      notices: response.meta?.notices ?? null,
      countryCode: response.meta?.country_code ?? null,
      markets: response.meta?.markets ?? [],
    };
  }

  async getLines(params: {
    category: string;
    brandId?: string | null;
    material?: string | null;
    axisValue?: string | null;
  }): Promise<ProductLine[]> {
    const query = new URLSearchParams({ category: params.category });
    if (params.brandId) query.set('brand_id', params.brandId);
    if (params.material) query.set('material', params.material);
    if (params.axisValue) query.set('axis_value', params.axisValue);

    const response = await this.httpGet<CatalogLinesResponse>(
      `/v1/catalog/lines?${query.toString()}`
    );

    return response.data;
  }

  /**
   * A page of products. Driven by search and by cursor: the catalogue is meant
   * to reach hundreds of thousands of rows, and a picker only walks forwards.
   */
  async getProducts(params: {
    category: string;
    search?: string;
    brandId?: string | null;
    lineId?: string | null;
    axisValue?: string | null;
    colorFamily?: string | null;
    cursor?: string | null;
  }): Promise<CatalogProductsPage> {
    // Built by hand rather than via the shared JSON:API helper: the helper
    // reserves `page` for the `page[number]` / `page[size]` form, and this
    // endpoint is a cursor paginator.
    const query = new URLSearchParams({ category: params.category });
    if (params.search) query.set('search', params.search);
    if (params.brandId) query.set('brand_id', params.brandId);
    if (params.lineId) query.set('line_id', params.lineId);
    if (params.axisValue) query.set('axis_value', params.axisValue);
    if (params.colorFamily) query.set('color_family', params.colorFamily);
    if (params.cursor) query.set('cursor', params.cursor);

    const response = await this.httpGet<CatalogProductsResponse>(
      `/v1/catalog/products?${query.toString()}`
    );

    return {
      products: response.data,
      nextCursor: response.meta?.next_cursor ?? null,
      notices: response.meta?.notices ?? null,
    };
  }

  // No id lookup here on purpose. `GET /v1/catalog/products?ids=` exists and
  // Apple and Android use it, but WEB re-hydrates a saved pick through
  // `/v1/brand-selections`, which answers for every level of the hierarchy and
  // not just for products. A second way to ask the same question is how the two
  // rows drift apart.
}

export const catalogService = new CatalogService();
